import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { Trash2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { DiscordMessagePreview } from '@/components/DiscordMessagePreview';
import { MediaPicker } from '@/components/MediaPicker';
import { BackLink, type Settings } from './_shared';

/**
 * Notifications-Config — lives in the TOP-LEVEL `socialMediaNotifications`
 * JSON column, not in moduleConfigs. Shape (per platform):
 *   { channels: [name1, name2, ...], channelId: <discord channel id> }
 * All subscriptions for a platform post into the same Discord channel.
 *
 * We fetch settings directly here instead of via useConfigPage because that
 * hook only surfaces moduleConfigs.
 */
interface Platform {
  channels: string[];
  channelId: string | null;
  imageUrl?: string | null;
}
const emptyPlatform = (): Platform => ({ channels: [], channelId: null, imageUrl: null });

export function NotificationsConfig() {
  const { guildId } = useParams<{ guildId: string }>();
  const qc = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ['settings', guildId],
    queryFn: () => api<{ settings: Settings & { socialMediaNotifications?: any } }>(
      `/api/guilds/${guildId}/settings`
    ),
    enabled: !!guildId,
  });

  const [twitch, setTwitch] = useState<Platform>(emptyPlatform());
  const [youtube, setYoutube] = useState<Platform>(emptyPlatform());
  const [newTwitchName, setNewTwitchName] = useState('');
  const [newYoutubeId, setNewYoutubeId] = useState('');

  useEffect(() => {
    const smn = settingsQuery.data?.settings.socialMediaNotifications || {};
    setTwitch({
      channels: Array.isArray(smn.twitch?.channels) ? smn.twitch.channels : [],
      channelId: smn.twitch?.channelId ?? null,
      imageUrl: smn.twitch?.imageUrl ?? null,
    });
    setYoutube({
      channels: Array.isArray(smn.youtube?.channels) ? smn.youtube.channels : [],
      channelId: smn.youtube?.channelId ?? null,
      imageUrl: smn.youtube?.imageUrl ?? null,
    });
  }, [settingsQuery.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      api(`/api/guilds/${guildId}/settings`, {
        method: 'PATCH',
        body: JSON.stringify({
          socialMediaNotifications: { twitch, youtube },
        }),
      }),
    // ['guildSettings'] feeds the ServerLayout sidebar (configured-dots).
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', guildId] });
      qc.invalidateQueries({ queryKey: ['guildSettings', guildId] });
    },
  });

  if (settingsQuery.isLoading) return <p className="text-muted-foreground">Lade…</p>;

  return (
    <div className="mx-auto max-w-2xl">
      <BackLink guildId={guildId!} />
      <h1 className="mb-6 text-2xl font-semibold">Notifications — Konfiguration</h1>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">Twitch</CardTitle>
          <CardDescription>
            Wenn einer der eingetragenen Streamer live geht, postet der Bot
            eine Meldung in den unten gewählten Discord-Channel.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Ziel-Channel (wo Meldungen erscheinen)</Label>
            <ChannelPicker
              guildId={guildId!}
              value={twitch.channelId}
              onChange={(v) => setTwitch({ ...twitch, channelId: v })}
              types={[CHANNEL_TYPE.GUILD_TEXT]}
            />
          </div>
          <div>
            <Label>Großes Bild URL (optional)</Label>
            <div className="flex gap-2">
              <Input
                value={twitch.imageUrl ?? ''}
                onChange={(e) => setTwitch({ ...twitch, imageUrl: e.target.value || null })}
                placeholder="https://…"
              />
              <MediaPicker
                guildId={guildId!}
                currentUrl={twitch.imageUrl ?? undefined}
                onSelect={(url) => setTwitch({ ...twitch, imageUrl: window.location.origin + url })}
              />
            </div>
          </div>
          <SubscriptionList
            items={twitch.channels}
            onRemove={(name) =>
              setTwitch({ ...twitch, channels: twitch.channels.filter((c) => c !== name) })
            }
          />
          <div className="flex gap-2">
            <Input
              value={newTwitchName}
              onChange={(e) => setNewTwitchName(e.target.value)}
              placeholder="Twitch-Username"
            />
            <Button
              disabled={!newTwitchName}
              onClick={() => {
                if (twitch.channels.includes(newTwitchName)) return;
                setTwitch({ ...twitch, channels: [...twitch.channels, newTwitchName] });
                setNewTwitchName('');
              }}
            >
              +
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">YouTube</CardTitle>
          <CardDescription>
            Neue Videos der eingetragenen Channel-IDs werden gemeldet
            (RSS-Poll jede Minute, Videos die älter als 24h sind werden ignoriert).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Ziel-Channel</Label>
            <ChannelPicker
              guildId={guildId!}
              value={youtube.channelId}
              onChange={(v) => setYoutube({ ...youtube, channelId: v })}
              types={[CHANNEL_TYPE.GUILD_TEXT]}
            />
          </div>
          <div>
            <Label>Großes Bild URL (optional)</Label>
            <div className="flex gap-2">
              <Input
                value={youtube.imageUrl ?? ''}
                onChange={(e) => setYoutube({ ...youtube, imageUrl: e.target.value || null })}
                placeholder="https://…"
              />
              <MediaPicker
                guildId={guildId!}
                currentUrl={youtube.imageUrl ?? undefined}
                onSelect={(url) => setYoutube({ ...youtube, imageUrl: window.location.origin + url })}
              />
            </div>
          </div>
          <SubscriptionList
            items={youtube.channels}
            onRemove={(id) =>
              setYoutube({
                ...youtube,
                channels: youtube.channels.filter((c) => c !== id),
              })
            }
          />
          <div className="flex gap-2">
            <Input
              value={newYoutubeId}
              onChange={(e) => setNewYoutubeId(e.target.value)}
              placeholder="YouTube Channel ID (UCxxxxx…)"
            />
            <Button
              disabled={!newYoutubeId}
              onClick={() => {
                if (youtube.channels.includes(newYoutubeId)) return;
                setYoutube({ ...youtube, channels: [...youtube.channels, newYoutubeId] });
                setNewYoutubeId('');
              }}
            >
              +
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Channel-ID findest du im YouTube-Studio → Einstellungen → Kanal → Erweiterte Einstellungen.
          </p>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-3">
        {saveMut.isSuccess && <span className="text-sm text-success">Gespeichert ✓</span>}
        {saveMut.error && (
          <span className="text-sm text-destructive">
            Fehler: {(saveMut.error as Error).message}
          </span>
        )}
        <Button disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>
          {saveMut.isPending ? 'Speichere…' : 'Speichern'}
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <DiscordMessagePreview
          hint="Twitch — sobald ein Streamer live geht."
          content={'🔥 xqc ist **LIVE** auf Twitch!\nhttps://twitch.tv/xqc'}
        />
        <DiscordMessagePreview
          hint="YouTube — bei neuem Video."
          content={'**MrBeast** hat ein neues Video veröffentlicht!\nhttps://youtube.com/watch?v=xxxxx'}
        />
      </div>
    </div>
  );
}

function SubscriptionList({
  items,
  onRemove,
}: {
  items: string[];
  onRemove: (name: string) => void;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">Keine Abonnements.</p>;
  }
  return (
    <ul className="space-y-1">
      {items.map((name) => (
        <li key={name} className="flex items-center justify-between rounded-md border p-2 text-sm">
          <code className="truncate">{name}</code>
          <Button variant="ghost" size="icon" onClick={() => onRemove(name)} title="Entfernen">
            <Trash2 className="h-4 w-4" />
          </Button>
        </li>
      ))}
    </ul>
  );
}
